/// <amd-module name="Repos/Views/Settings/Options/Services/OptionsViewService" />
import { PolicyConfiguration } from "Repos/Common/Contracts/Policy";
import { GitRepositoryPermissions, IReposPermissionsService } from "Repos/Common/Permissions";
import { refToBranchOrTagName } from "Repos/Common/Util/Ref";
import { ISettingsViewService } from "Repos/Views/Settings/Common/Services/SettingsViewService";
import * as Resources from "Repos/Views/Settings/Options/Resources";
import { IObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { localeComparer } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { issueRequest } from "VSS/Platform/Fetch";
import { IVssPageService } from "VSS/Platform/Page";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { IBranch, IOption, IRepositoryOption } from "../Contracts/Contracts";
import { getGitClient } from "../RestClient/Git";
import { getPolicyClient } from "../RestClient/Policy";
import { getSettingsClient } from "../RestClient/Settings";

interface IOptionsReponse {
    __wrappedArray?: IRepositoryOption[];
}

interface IScope {
    repositoryId: string;
}

interface ISearchableBranchesMaxCount {
    maxNumberOfConfigurableBranchesForSearch: number;
}

interface ISearchableBranchesSettings {
    scope: IScope[];
    searchBranches: string[];
}

interface IUpdateOption {
    key: string;
    textValue?: string;
    value: boolean;
}

export interface IOptionsViewService extends IVssService {
    checkPermissionsAsync(permission: GitRepositoryPermissions, projectId: string, repositoryId?: string): Promise<boolean>;
    getRepositoryOptions(projectId: string, repositoryId?: string, isTfvc?: boolean): Promise<IOption[]>;
    getSearchableBranches(
        projectId: string,
        repositoryId: string,
        defaultBranch?: string
    ): IObservableArray<IBranch | IReadonlyObservableValue<IBranch | undefined>>;
    getSearchableBranchesMaxCount(): number;
    toggleRepositoryOption(option: IOption, projectId: string, repositoryId?: string, textValue?: string): Promise<{ validationError?: string }>;
    updateSearchableBranches(projectId: string, repositoryId: string, branches: IBranch[]): Promise<IBranch[]>;
}

export class OptionsViewService extends VssService implements IOptionsViewService {
    private readonly permissionsCache: { [key: string]: Promise<boolean> } = {};
    private searchableBranchesPolicy: PolicyConfiguration | undefined;
    private settingsSvc: ISettingsViewService;

    public _serviceEnd = (pageContext: IVssPageContext): void => {
        super._serviceEnd(pageContext);
    };

    public _serviceStart = (pageContext: IVssPageContext): void => {
        super._serviceStart(pageContext);
        this.settingsSvc = this.pageContext.getService<ISettingsViewService>("SettingsViewService");
    };

    public checkPermissionsAsync = (permission: GitRepositoryPermissions, projectId: string, repositoryId?: string): Promise<boolean> => {
        const cacheKey = permission + (repositoryId || "");
        let result = this.permissionsCache[cacheKey];

        if (result === undefined) {
            const permissionService = this.pageContext.getService<IReposPermissionsService>("IReposPermissionsService");

            result = permissionService.hasPermissionAsync(permission, undefined, repositoryId || "", projectId);
            this.permissionsCache[cacheKey] = result;
        }

        return result;
    };

    public getRepositoryOptions = (projectId: string, repositoryId?: string, isTfvc?: boolean): Promise<IOption[]> => {
        if (isTfvc) {
            return this.getTfvcOptions(projectId);
        }

        return repositoryId ? this.repositoryOptions(projectId, repositoryId) : this.allGitRepositoriesOptions(projectId);
    };

    public getSearchableBranches = (
        projectId: string,
        repositoryId: string,
        defaultBranch?: string
    ): IObservableArray<IBranch | IReadonlyObservableValue<IBranch | undefined>> => {
        const loadingArr = new ObservableArray<IBranch | IReadonlyObservableValue<IBranch | undefined>>(
            new Array(5).fill(new ObservableValue<IBranch | undefined>(undefined))
        );
        const gitClient = getGitClient(this.pageContext);
        gitClient
            .getPolicyConfigurations(projectId, repositoryId, undefined, searchPolicyType)
            .catch(this.settingsSvc.showErrorMessage)
            .then(result => {
                if (result) {
                    const config = result.policyConfigurations;
                    this.searchableBranchesPolicy = (config && config[0]) ? config[0] : undefined;

                    loadingArr.value = getSearchBranches(this.searchableBranchesPolicy, defaultBranch);
                }
            });

        return loadingArr;
    };

    public getSearchableBranchesMaxCount = (): number => {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = contributionService.getData<ISearchableBranchesMaxCount>("ms.vss-code-web.version-control-admin-data-provider");

        if (data && data.maxNumberOfConfigurableBranchesForSearch !== undefined) {
            return data.maxNumberOfConfigurableBranchesForSearch;
        }

        return searchBranchesMaxCount; // fallback to default in case of any issues fetching actual max count
    };

    public toggleRepositoryOption = (
        option: IOption,
        projectId: string,
        repositoryId?: string,
        textValue?: string
    ): Promise<{ validationError?: string }> => {
        if (option.isTfvc) {
            return this.toggleTfvcOptions(option, projectId);
        }

        repositoryId = repositoryId || "00000000-0000-0000-0000-000000000000";
        const updateOption: IUpdateOption = {
            key: option.key,
            value: textValue ? true : !option.value,
            textValue
        };
        const url = this.getServiceUrl("UpdateRepositoryOption", projectId, repositoryId);
        const verificationInput = document.querySelector<HTMLInputElement>("input[name=__RequestVerificationToken]");
        const verificationToken = verificationInput ? verificationInput.value : "";
        const options: RequestInit = {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: `repositoryId=${encodeURIComponent(repositoryId)}&option=${encodeURIComponent(
                JSON.stringify(updateOption)
            )}&__RequestVerificationToken=${encodeURIComponent(verificationToken)}`
        };

        return issueRequest(url, options).then(
            () => {
                option.value = updateOption.value;
                return {};
            },
            error => {
                if (error.status === 400 && option.defaultTextValue) {
                    // Text options can receive validation errors that should be displayed in the TextField (without HTTP status).
                    const errorMessage: string = error.message || "";
                    const statusIndex = errorMessage.indexOf(": ");
                    const messageWithoutStatus = statusIndex >= 0 ? errorMessage.substr(statusIndex + 2) : errorMessage;
                    return { validationError: messageWithoutStatus };
                } else {
                    this.settingsSvc.showErrorMessage(error);
                    return {};
                }
            }
        );
    };

    public updateSearchableBranches = (projectId: string, repositoryId: string, branches: IBranch[]): Promise<IBranch[]> => {
        if (!this.searchableBranchesPolicy) {
            // policy doesnt exist so need to create
            return this.createPolicy(projectId, repositoryId, branches);
        } else {
            this.searchableBranchesPolicy.settings.searchBranches = branches.filter(x => !x.isDefault).map(branch => branch.ref);
            const policyClient = getPolicyClient(this.pageContext);

            return policyClient
                .updatePolicyConfiguration(this.searchableBranchesPolicy, projectId, this.searchableBranchesPolicy.id)
                .catch(this.settingsSvc.showErrorMessage)
                .then(data => {
                    if (data) {
                        branches.forEach(branch => (branch.config = data));
                    } else {
                        branches = branches.filter(branch => branch.isDefault || branch.config);
                    }
                    return branches;
                });
        }
    };

    private allGitRepositoriesOptions = (projectId: string): Promise<IOption[]> => {
        const url = this.getServiceUrl("AllGitRepositoriesOptions", projectId);
        const options: RequestInit = {
            method: "GET",
            headers: {
                Accept: "application/json, text/javascript"
            }
        };

        const links: { [key: string]: string } = {
            ["GravatarEnabled"]: "https://go.microsoft.com/fwlink/?LinkId=313945",
            ["DefaultBranchName"]: "https://go.microsoft.com/fwlink/?linkid=2134867"
        };

        return getResponse(url, options, this.settingsSvc.showErrorMessage).then((result: IOptionsReponse) =>
            result && result.__wrappedArray
                ? result.__wrappedArray.map<IOption>(wsOption => ({
                      title: wsOption.title,
                      category: wsOption.category,
                      key: wsOption.key,
                      description: wsOption.displayHtml,
                      defaultTextValue: wsOption.defaultTextValue,
                      textValue: wsOption.textValue,
                      value: wsOption.value,
                      link: wsOption.link || links[wsOption.key]
                  }))
                : []
        );
    };

    private createPolicy = (projectId: string, repositoryId: string, branches: IBranch[]): Promise<IBranch[]> => {
        const policyClient = getPolicyClient(this.pageContext);
        const creationConfig = getPolicyCreationObject(
            repositoryId,
            branches.filter(x => !x.isDefault).map(branch => branch.ref)
        );
        return policyClient
            .createPolicyConfiguration(creationConfig, projectId)
            .catch(this.settingsSvc.showErrorMessage)
            .then(data => {
                if (data) {
                    this.searchableBranchesPolicy = data;
                    branches.forEach(branch => (branch.config = data));
                    return branches;
                }

                return branches.filter(branch => branch.isDefault || branch.config);
            });
    };

    private getTfvcOptions = (projectId: string): Promise<IOption[]> => {
        const settingsClient = getSettingsClient(this.pageContext);
        return settingsClient
            .getEntriesForScope("host", "project", projectId, tfvcOptionRoute)
            .catch(this.settingsSvc.showErrorMessage)
            .then(result => {
                const option: IOption = createTfvcOption();
                if (result && result[tfvcWebEditableKey] !== undefined) {
                    option.value = result[tfvcWebEditableKey];
                }

                return [option];
            });
    };

    private getServiceUrl = (route: string, projectId: string, repositoryId?: string): string => {
        const hostPath = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!.hostPath || "";
        const repositoryUrlPart = repositoryId ? `&repositoryId=${repositoryId}` : "";
        const url = combineUrlPaths(encodeURI(hostPath), `${projectId}/_api/_versioncontrol/${route}?__v=5${repositoryUrlPart}`);
        return url;
    };

    private repositoryOptions = (projectId: string, repositoryId: string): Promise<IOption[]> => {
        const url = this.getServiceUrl("RepositoryOptions", projectId, repositoryId);
        const options: RequestInit = {
            method: "GET",
            headers: {
                Accept: "application/json, text/javascript"
            }
        };

        return getResponse(url, options, this.settingsSvc.showErrorMessage).then((result: IOptionsReponse) => {
            if (result && result.__wrappedArray) {
                const arr: IOption[] = result.__wrappedArray.map(wsOption => {
                    const option: IOption = {
                        title: wsOption.title,
                        category: wsOption.category,
                        key: wsOption.key,
                        description: wsOption.displayHtml,
                        value: wsOption.value,
                        link: wsOption.link,
                        parentOptionKey: wsOption.parentOptionKey,
                        textValue: wsOption.textValue
                    };

                    return option;
                });
                return arr;
            }
            return [];
        });
    };

    private toggleTfvcOptions = (option: IOption, projectId: string): Promise<{ validationError?: string }> => {
        const settingsClient = getSettingsClient(this.pageContext);
        const key = `${tfvcOptionRoute}/${tfvcWebEditableKey}`;
        const setting = { [key]: !option.value };
        return settingsClient.setEntriesForScope(setting, "host", "project", projectId).then(
            () => {
                option.value = !option.value;
                return {};
            },
            error => {
                this.settingsSvc.showErrorMessage(error);
                return {};
            }
        );
    };
}

const searchBranchesMaxCount = 5;
const searchPolicyType = "0517f88d-4ec5-4343-9d26-9930ebd53069";
const creationPolicy = {
    type: { id: searchPolicyType },
    isBlocking: false,
    isEnabled: false,
    settings: {
        scope: [{ repositoryId: "" }],
        searchBranches: undefined
    }
} as PolicyConfiguration;

const tfvcOption: IOption = {
    key: "",
    value: true,
    description: Resources.WebEditableDescription,
    title: Resources.WebEditableTitle,
    category: '',
    isTfvc: true
};
const tfvcOptionRoute = "VersionControl/AdminOptions";
const tfvcWebEditableKey = "Tfvc/WebEditEnabled";

function createTfvcOption(): IOption {
    return { ...tfvcOption };
}

function getPolicyCreationObject(repositoryId: string, refs: string[]) {
    const creationObject: PolicyConfiguration = { ...creationPolicy };
    const settings: ISearchableBranchesSettings = creationObject.settings;
    settings.scope[0].repositoryId = repositoryId;
    settings.searchBranches = refs;

    return creationObject;
}

async function getResponse(url: string, options: RequestInit, errorHandler: (message: string) => void) {
    try {
        const response = await issueRequest(url, options);
        const text = await response.text();
        return JSON.parse(text);
    } catch (error) {
        errorHandler(error);
    }
}

function getSearchBranches(config?: PolicyConfiguration, defaultBranch?: string) {
    let result: IBranch[] = [];
    let defaultIncluded = false;

    if (config?.settings?.searchBranches?.length) {
        const branches: string[] = config.settings.searchBranches;
        result = branches.map(x => {
            const isDefault = defaultBranch === x;
            const branch: IBranch = {
                name: refToBranchOrTagName(x),
                ref: x,
                isDefault: isDefault,
                config: config
            };
            defaultIncluded = defaultIncluded || isDefault;

            return branch;
        });
    }

    if (!defaultIncluded && defaultBranch) {
        result.push({ name: refToBranchOrTagName(defaultBranch), isDefault: true, ref: defaultBranch });
    }

    result = result.sort((a, b) => {
        if (a.isDefault) {
            return -1;
        }

        if (b.isDefault) {
            return 1;
        }

        return localeComparer(a.name, b.name);
    });

    return result;
}

Services.add("OptionsViewService", { serviceFactory: OptionsViewService });
